import { useCallback, useRef, useState, type ReactNode } from "react";
import { APP_NAV, APP_NAV_LOG_OUT, BRAND, GUEST_SESSION } from "../design/copy.js";
import { GuestSessionLightbox } from "./guest-session-lightbox.js";
import { Lettering, NotYet } from "./primitives.js";
import type { AuthMode, RouteName } from "../routes.js";

/**
 * THE SITE HEADER (2026-09-22): one slim row above every screen — the `P&C`
 * mark on the house plum, the wordmark beside it, the menu along the rest of
 * the row, a hairline under all of it.
 *
 * It replaced the menu that used to collapse to its mark inside each page's
 * own chrome. It began as the same accordion; on 2026-09-23 Andre had it stay
 * OPEN — there is room for four words on this row — so the list is always
 * there, in the tab order, and the mark is a plain door home, the same as
 * the wordmark. Navigation is the one honestly non-diegetic element on any
 * screen, so it is still the smallest thing on it: a 52px row the
 * compositions under it pay for out of the viewport (`--pc-header-h`).
 *
 * "Log out." closes the row, top right (2026-09-23), for a signed-in account
 * only: since the landing page sends a signed-in learner home, this is the
 * way back to it. A guest is not offered it (app.tsx says why).
 *
 * "Guest session." stands in the same place for a guest (2026-09-24, Andre):
 * it opens a paper that says what a guest session keeps and for how long,
 * with the two doors to keeping it — "log in." and "create an account." —
 * each landing on the auth screen's matching tab. Nobody sees both words.
 */
const DESTINATIONS: Record<string, RouteName | null> = {
  home: "home",
  profile: "profile",
  coach: "coach",
  library: "library",
  collaborate: null,
};

export function SiteHeader({
  onNavigate,
  current,
  onLogOut,
  onGuestAuth,
}: {
  onNavigate: (r: RouteName) => void;
  current: RouteName;
  onLogOut?: (() => void) | undefined;
  /** Present only for a guest: where the guest paper's two doors go. */
  onGuestAuth?: ((mode: AuthMode) => void) | undefined;
}): ReactNode {
  const [guestPaperOpen, setGuestPaperOpen] = useState(false);
  const guestButton = useRef<HTMLButtonElement | null>(null);
  const putGuestPaperDown = useCallback(() => {
    setGuestPaperOpen(false);
    guestButton.current?.focus();
  }, []);

  return (
    <header className="pc-site-header" data-region="site-header">
      <nav className="pc-site-header-nav" aria-label="Main">
        <button type="button" className="pc-site-mark" data-press="box" aria-label="Home" onClick={() => onNavigate("home")}>
          <Lettering text={BRAND.short} />
        </button>
        <button type="button" className="pc-site-wordmark" onClick={() => onNavigate("home")}>
          <Lettering text={`${BRAND.wordmarkLine1} ${BRAND.wordmarkLine2}`} />
        </button>
        <ul id="pc-site-nav-list" className="pc-site-nav-list">
          {APP_NAV.map((item) => {
            const destination = DESTINATIONS[item.id] ?? null;
            return (
              <li key={item.id}>
                {destination === null ? (
                  <NotYet>{item.label}</NotYet>
                ) : (
                  <button
                    type="button"
                    onClick={() => onNavigate(destination)}
                    aria-current={current === destination || (destination === "profile" && current === "coach") || (destination === "library" && current === "book") ? "page" : undefined}
                  >
                    <Lettering text={item.label} />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
        {/* Outside the list, so a narrow screen scrolls the menu and never this. */}
        {onLogOut === undefined ? null : (
          <button type="button" className="pc-site-log-out" onClick={onLogOut}>
            <Lettering text={APP_NAV_LOG_OUT} />
          </button>
        )}
        {onLogOut !== undefined || onGuestAuth === undefined ? null : (
          <button
            ref={guestButton}
            type="button"
            className="pc-site-guest"
            aria-haspopup="dialog"
            aria-expanded={guestPaperOpen}
            onClick={() => setGuestPaperOpen(true)}
          >
            <Lettering text={GUEST_SESSION.button} />
          </button>
        )}
      </nav>
      {guestPaperOpen && onGuestAuth !== undefined ? (
        <GuestSessionLightbox
          onClose={putGuestPaperDown}
          onDoor={(mode) => {
            setGuestPaperOpen(false);
            onGuestAuth(mode);
          }}
        />
      ) : null}
    </header>
  );
}
